import { useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'

import Chevron from 'shared/svg/chevron.svg'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { track, withContextualizedTracking } from 'lib/analytics/track'
import ProductCard from '../../../product-card'
import css from './ProductCarousel.module.scss'
import { mapHeroesToProductCards } from '../../../product-grid'
import { V3HeroProductJson } from './ProductCarousel.d'
import { useDynamicLinkTracking } from '../hooks'

export interface ProductCarouselProps {
  contentfulId: string
  title: string
  slot: number
  subText: string
  products: V3HeroProductJson[]
}

const ProductCount = 12

const COMPONENT_TYPE = 'product_carousel'

const CarouselProductCard = withContextualizedTracking({
  eventLocation: track.EventLocation.STORE_MINUS_CAROUSEL,
})(ProductCard)

const ProductCarousel = ({
  contentfulId,
  title,
  slot,
  subText,
  products: untransformedProducts,
}: ProductCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [productCardWidth, setProductCardWidth] = useState(200)

  const productListRef = useRef<HTMLDivElement>(null)
  const productSlideRef = useRef<HTMLLIElement>(null)

  const [currentUser] = useCurrentUser()
  const registryProps = {
    isRegistryOwner: !!currentUser?.registryId,
    registryOwnerId: currentUser?.id,
    registryId: currentUser?.registryId,
  }

  useEffect(() => {
    if (productSlideRef.current) {
      const computedStyles = window.getComputedStyle(productSlideRef.current)
      const width =
        productSlideRef.current.offsetWidth +
        parseFloat(computedStyles.marginLeft) +
        parseFloat(computedStyles.marginRight)
      setProductCardWidth(width)
    }
  }, [])

  const changeActiveIndex = (index: number) => {
    let newIndex = activeIndex + index

    let maxViewableCards = 0
    if (productListRef.current && productCardWidth) {
      maxViewableCards =
        Math.floor(productListRef.current.offsetWidth / productCardWidth) - 1
    }

    if (newIndex >= untransformedProducts.length - maxViewableCards) {
      newIndex = 0
    }
    if (newIndex < 0) {
      newIndex = untransformedProducts.length - 1
    }
    setActiveIndex(newIndex)
  }

  const handleProductShiftRight = () => changeActiveIndex(-1)
  const handleProductShiftLeft = () => changeActiveIndex(1)

  const handlers = useSwipeable({
    onSwipedLeft: handleProductShiftLeft,
    onSwipedRight: handleProductShiftRight,
  })

  const { tracker, handleLinkClicked } = useDynamicLinkTracking((link) => ({
    event: track.componentClicked,
    componentType: COMPONENT_TYPE,
    contentId: contentfulId,
    contentName: title,
    slotPosition: slot,
    urlClicked: link.href,
    ...registryProps,
  }))

  const handleProductClicked = (product: any, productIndex: number) => {
    tracker.trackEvent({
      event: track.componentClicked,
      componentType: COMPONENT_TYPE,
      contentId: contentfulId,
      contentName: title,
      positionInList: productIndex,
      slotPosition: slot,
      urlClicked: new URL(product.path, window.location.origin).toString(),
      ...registryProps,
    })
  }

  const products = untransformedProducts?.map((product: V3HeroProductJson) =>
    mapHeroesToProductCards(product)
  )

  if (products.length < ProductCount) {
    return null
  }

  return (
    <section
      className={css.ProductCarousel}
      data-testid="ProductCarousel"
      {...handlers}
    >
      <div className={`${css.TitleCard} ${css.MobileOnly}`}>
        <h2>{title}</h2>
        {/* eslint-disable-next-line react/no-danger,jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
        <p
          dangerouslySetInnerHTML={{ __html: subText }}
          onClick={handleLinkClicked}
        />
      </div>

      <div className={css.ProductList} ref={productListRef}>
        <ul
          className={css.ProductListSlider}
          data-testid="ProductSlider"
          role="group"
          style={{
            transform: `translateX(-${productCardWidth * activeIndex}px)`,
          }}
        >
          <li
            className={`${css.TitleCard} ${css.DesktopOnly}`}
            key="title-card-element"
          >
            <h2>{title}</h2>
            {/* eslint-disable-next-line react/no-danger,jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
            <p
              dangerouslySetInnerHTML={{ __html: subText }}
              onClick={handleLinkClicked}
            />
          </li>

          {products.map((product, i) => (
            <li
              aria-label={`slide ${i + 1} of ${products.length}`}
              className={css.ProductCardWrapper}
              // eslint-disable-next-line react/no-array-index-key
              key={`${product.productId}-${i}`}
              ref={productSlideRef}
            >
              <CarouselProductCard
                className={css.ProductCard}
                position={i + 1}
                {...product}
                lazyLoad={false}
                onClick={() => handleProductClicked(product, i + 1)}
                {...registryProps}
              />
            </li>
          ))}
        </ul>

        {activeIndex > 0 && (
          <button
            aria-hidden="true"
            className={`${css.ScrollButton} ${css.Left}`}
            data-testid="ScrollButton"
            type="button"
            onClick={handleProductShiftRight}
          >
            <Chevron className={css.Chevron} />
          </button>
        )}

        <button
          aria-hidden="true"
          className={`${css.ScrollButton} ${css.Right}`}
          data-testid="ScrollButton"
          type="button"
          onClick={handleProductShiftLeft}
        >
          <Chevron className={css.Chevron} />
        </button>
      </div>
    </section>
  )
}

export default ProductCarousel
